import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionLabel, MaskedLine } from "@/components/Reveal";
import WaitlistForm from "@/components/WaitlistForm";
import Countdown from "@/components/Countdown";

const SPECS = [
  { k: "CAPACITY", v: "32L" },
  { k: "SHELL", v: "1680D Ballistic" },
  { k: "POCKETS", v: "43" },
  { k: "WEIGHT", v: "2.4 kg" },
];

const DETAILS = [
  { img: "/images/detail_logo.png", title: "Rubber CHWA Patch", body: "Debossed, matte, permanent. The mark of the standard." },
  { img: "/images/detail_zipper.png", title: "Heavy-Duty Zippers", body: "Oversized and glove-friendly. Rated for tens of thousands of cycles." },
  { img: "/images/pack_open.png", title: "Modular Storage", body: "A clamshell interior with 43 purpose-built pockets. Every tool visible." },
];

export default function Featured({ onDealer }) {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="featured" data-testid="featured-section" className="relative overflow-hidden border-t border-zinc-800">
      <div className="spotlight absolute inset-0" />
      <div className="relative mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-40">
        <Reveal>
          <SectionLabel index="04" title="Featured Product" />
        </Reveal>

        <div className="mt-12 text-center md:mt-16">
          <MaskedLine delay={0.05}>
            <p className="text-xs uppercase tracking-[0.35em] text-zinc-500">The Flagship</p>
          </MaskedLine>
          <MaskedLine delay={0.15}>
            <h2 data-testid="featured-product-name" className="font-display text-[clamp(3.5rem,12vw,12rem)] font-bold uppercase leading-[0.9] tracking-tight text-zinc-50">
              CHWA-01
            </h2>
          </MaskedLine>
          <MaskedLine delay={0.25}>
            <p className="font-display text-xl uppercase tracking-[0.2em] text-zinc-400 sm:text-2xl">The Standard Pack</p>
          </MaskedLine>
        </div>

        <div ref={imgRef} className="relative mx-auto mt-14 max-w-4xl md:mt-20">
          <Reveal y={40}>
            <div className="clip-frame relative overflow-hidden border border-zinc-800 bg-[#0D0D0D]">
              <motion.img
                src="/images/pack_front.png"
                alt="CHWA-01 Standard Pack — premium black tool backpack"
                data-testid="featured-product-image"
                style={{ y: imgY }}
                className="h-[420px] w-full scale-110 object-cover md:h-[640px]"
              />
            </div>
          </Reveal>
        </div>

        <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 border border-zinc-800 md:mt-20 md:grid-cols-4">
          {SPECS.map((s, i) => (
            <Reveal key={s.k} delay={0.06 * i} y={16}>
              <div data-testid={`featured-spec-${s.k.toLowerCase()}`} className="border-zinc-800 px-6 py-8 text-center [&:not(:last-child)]:border-r max-md:[&:nth-child(-n+2)]:border-b max-md:[&:nth-child(2)]:border-r-0">
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{s.k}</p>
                <p className="mt-3 font-display text-2xl font-semibold uppercase text-zinc-50 md:text-3xl">{s.v}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid gap-4 md:mt-28 md:grid-cols-3">
          {DETAILS.map((d, i) => (
            <Reveal key={d.title} delay={0.08 * i} y={24}>
              <div data-testid={`featured-detail-${i}`} className="group">
                <div className="clip-frame relative overflow-hidden border border-zinc-800">
                  <img
                    src={d.img}
                    alt={d.title}
                    className="h-72 w-full object-cover opacity-80 transition-[opacity,transform] duration-700 group-hover:scale-[1.03] group-hover:opacity-100"
                  />
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold uppercase tracking-tight text-zinc-100">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-500">{d.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20 text-center md:mt-28">
          <p className="font-display text-3xl font-semibold uppercase tracking-tight text-zinc-50 sm:text-4xl lg:text-5xl">
            Built for everyday work.
          </p>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-zinc-500">
            Be first in line for first allocation.
          </p>

          <div className="mt-10">
            <Countdown />
          </div>

          <div className="mt-10">
            <WaitlistForm source="featured" />
          </div>

          <button
            data-testid="featured-dealer-cta"
            onClick={onDealer}
            className="mt-6 text-xs uppercase tracking-[0.25em] text-zinc-500 underline-offset-4 transition-colors duration-300 hover:text-zinc-100 hover:underline"
          >
            Dealers get first allocation — Become a Dealer
          </button>
        </Reveal>
      </div>
    </section>
  );
}
